import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import { FormattedMessage } from 'react-intl';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { FloatInput } from 'ui-components/components/float-input';
import { emptyArray } from 'ui-components/default-props/empty';

import style from './edit-filterview-modal.module.less';
import { messages } from './messages';
import { ErrorMessage } from './error-message';

const doesExistingViewHaveName = (name, savedFilterViews) => savedFilterViews.some((view) => view.displayName === name);

const NoViewNameErrorMessage = <FormattedMessage {...messages.noViewNameErrorMessage} />;

const ExistingViewNameErrorMessage = <FormattedMessage {...messages.existingViewNameErrorMessage} />;

export const EditFilterViewModal = ({
  deleteViewModalAction,
  saveExistingFilterViewRequest,
  activeFilterView,
  savedFilterViews = emptyArray,
  closeModal,
}) => {
  const [newViewName, setNewViewName] = useState(activeFilterView.displayName);
  const [errorMessage, setErrorMessage] = useState();

  const renameFilterView = useCallback(() => {
    if (doesExistingViewHaveName(newViewName, savedFilterViews)) {
      setErrorMessage('saveError');
      return;
    }

    if (newViewName === '') {
      setErrorMessage('noNameError');
      return;
    }
    const viewWithNewFilters = {
      ...activeFilterView,
      displayName: newViewName,
    };
    saveExistingFilterViewRequest(viewWithNewFilters);
    closeModal();
  }, [activeFilterView, closeModal, newViewName, saveExistingFilterViewRequest, savedFilterViews]);

  const handleDeleteButtonClick = useCallback(() => {
    deleteViewModalAction();
    closeModal();
  }, [closeModal, deleteViewModalAction]);

  const changeViewName = useCallback((value) => {
    setNewViewName(value);
    setErrorMessage();
  }, []);

  return (
    <Modal className={style['edit-filterview-modal']}>
      <ModalHeader closeModal={closeModal}>
        <FormattedMessage {...messages.editViewModalHeader} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div className={style['modal-content']}>
          <FloatInput
            data-testid='new-view-name'
            label={<FormattedMessage {...messages.inputFloatLabel} />}
            maxLength={50}
            onChange={changeViewName}
            value={newViewName}
          />
          {errorMessage && <ErrorMessage message={errorMessage === 'saveError' ? ExistingViewNameErrorMessage : NoViewNameErrorMessage} />}
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <div>
          <button className={style['footer-content-left']} data-testid='delete-button' onClick={handleDeleteButtonClick}>
            <FormattedMessage {...messages.deleteButtonText} />
          </button>
        </div>
        <Button className={style['cancel-button']} onClick={closeModal} theme='tertiary'>
          <FormattedMessage {...messages.cancelButtonText} />
        </Button>
        <Button onClick={renameFilterView} testId='save-button' theme='primary'>
          <FormattedMessage {...messages.saveButtonText} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

EditFilterViewModal.propTypes = {
  deleteViewModalAction: PropTypes.func.isRequired,
  saveExistingFilterViewRequest: PropTypes.func.isRequired,
  activeFilterView: PropTypes.object.isRequired,
  savedFilterViews: PropTypes.array,
  closeModal: PropTypes.func.isRequired,
};
